<template>
  <div>
    <!-- Confirmation Modal -->
    <ConfirmationModal
      :title="confirmationTitle"
      :message="confirmationMessage"
      confirmColor="error darken-1"
      ref="confirmationModal"
    />

    <!-- Purpose Modal-->
    <v-dialog width="480px" :overlay-opacity="0.9" v-model="purposeDialog">
      <v-card>
        <v-card-title class="d-block">
          <div class="font-weight-bold">
            {{ item.id ? 'Update' : 'Add' }} Purpose
          </div>
          <p class="caption mb-4">Enter purpose details below.</p>
        </v-card-title>
        <v-card-text class="py-2">
          <v-form @submit.prevent="saveItem()">
            <ValidationObserver ref="validationObserver">
              <ValidationProvider
                name="Purpose"
                :rules="'required'"
                v-slot="{ errors }"
              >
                <v-select
                  outlined
                  dense
                  :autofocus="true"
                  label="Purpose"
                  placeholder="Select loan purpose"
                  :items="unUsedLoanPurposeTypes"
                  item-code="name"
                  item-text="name"
                  v-model="item.purpose"
                  :error-messages="errors"
                >
                  <template v-slot:label>
                    Purpose
                    <span class="red--text">*</span>
                  </template>
                </v-select>
              </ValidationProvider>

              <ValidationProvider
                name="Allocated Amount"
                :rules="`required|min_value:0|max_value:${remainingPortion}`"
                v-slot="{ errors }"
              >
                <vuetify-money
                  type="number"
                  outlined
                  dense
                  required
                  label="Allocated Amount"
                  :hint="`${asMoney(remainingPortion)} left to be allocated.`"
                  persistent-hint
                  v-model="item.amount"
                  :error-messages="errors"
                  :options="VuetifyMoneyOptions"
                >
                </vuetify-money>
              </ValidationProvider>

              <v-checkbox
                v-model="item.isMainPurpose"
                label="Main Loan Purpose"
              />
            </ValidationObserver>

            <v-card-actions class="pt-5 justify-end">
              <v-btn
                @click="closeModal()"
                min-width="120"
                min-height="42"
                depressed
                class="text-capitalize"
                >Cancel</v-btn
              >
              <v-btn
                type="submit"
                min-width="120"
                min-height="42"
                depressed
                color="primary"
                class="text-capitalize"
                >Save</v-btn
              >
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-row align="center" justify="space-between">
      <v-col cols="12" md="9">
        <h4>Loan Purposes</h4>
        <p class="mb-0">Loan purposes and allocated amounts.</p>
      </v-col>
      <v-col cols="12" md="3" class="text-md-right">
        <v-btn
          @click="openModal()"
          class="text-capitalize shadow"
          outlined
          text
          color="primary"
          v-if="!isTotalPurposeEqualToLoanAmount"
        >
          <v-icon>mdi-plus</v-icon>
          <span class="hidden">Add</span></v-btn
        >
      </v-col>
    </v-row>

    <!-- Loan Purpose Card -->
    <v-card
      class="mt-5 pa-2 px-0 shadow"
      v-for="item in loan.loanPurposes"
      :key="item.id"
    >
      <v-card-text class="d-flex justify-space-between pa-0">
        <!-- Card Details -->
        <div>
          <h4 class="text-capitalize text-muted">
            {{ item.purpose }} /
            {{ item.isMainPurpose ? 'Main Purpose' : 'Other Purpose' }}
          </h4>

          <p class="mb-0 pb-0">{{ asMoney(item.amount) }}</p>
        </div>

        <!-- Card Actions -->
        <div class="d-flex flex-column justify-center">
          <div>
            <v-btn
              @click="removeItem(item)"
              class="text-capitalize ml-1"
              text
              small
            >
              Delete</v-btn
            >
            <v-btn
              @click="openModal(item)"
              class="text-capitalize mr-1"
              color="primary"
              outlined
              text
              small
            >
              Edit</v-btn
            >
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import PageMixin from '@/mixins/page.mixin';
import { VuetifyMoneyOptions } from '@/lib/constants/options';
import { cloneDeep } from 'lodash';
import FormatMixin from '@/mixins/format.mixin';
import { v4 } from 'uuid';

export default {
  mixins: [PageMixin, FormatMixin],

  data() {
    return {
      /** @type {import('vuetify').DataTableHeader[]}*/
      headers: [
        { text: 'Purpose', value: 'accountName', sortable: false },
        { text: 'Amount', value: 'amount', sortable: false },
        { text: 'Main Purpose', value: 'isMainPurpose', sortable: false },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false }
      ],
      VuetifyMoneyOptions: VuetifyMoneyOptions,

      purposeDialog: false,
      confirmationTitle: '',
      confirmationMessage: '',

      item: {},
      purposes: []
    };
  },

  watch: {
    /**
     * Watch loan prop and update local loan purposes
     */
    loan: {
      deep: true,
      immediate: true,
      handler: function (val) {
        this.purposes = val.loanPurposes || [];
      }
    }
  },

  computed: {
    /**
     * Get total loan amount
     */
    totalLoanAmount: function () {
      const amount =
        +(this.loan.totalInternalLoanAmount || 0) +
        +(this.loan.totalExternalLoanAmount || 0) +
        +(this.loan.totalConsolidatedAmount || 0) +
        +(this.loan.totalRollUp || 0);
      return amount;
    },

    /**
     * Get a list of purposes that has not been used on this loan
     */
    unUsedLoanPurposeTypes: function () {
      return (
        this.loanPurposeTypes?.filter(
          (e) =>
            !this.purposes.some(
              (p) => p.purpose === e.name && p.purpose != this.item.purpose
            )
        ) ?? []
      );
    },

    /**
     * Get total loan purposes amount
     */
    totalPurposeAmount: function () {
      return this.purposes.reduce((acc, cur) => acc + (cur.amount || 0), 0);
    },

    /**
     * Get total loan purposes amount
     */
    isTotalPurposeEqualToLoanAmount: function () {
      return (
        +Number(this.totalLoanAmount).toFixed(2) ==
        +Number(this.totalPurposeAmount).toFixed(2)
      );
    },

    /**
     * Remainder left to be allocated to a purpose
     */
    remainingPortion: function () {
      const currentPurpose = this.item.purpose;

      const totalPurposeAmount = this.purposes
        .filter((e) => e.purpose != currentPurpose)
        .reduce((acc, cur) => acc + (cur.amount || 0), 0);

      return (
        +Number(this.totalLoanAmount).toFixed(2) -
        +Number(totalPurposeAmount).toFixed(2)
      );
    }
  },

  props: {
    /**
     * loan object
     */
    loan: {
      type: Object,
      default: () => ({ purposes: [] })
    },

    /**
     * list of loan purposes
     */
    loanPurposeTypes: {
      type: Array,
      default: () => []
    }
  },

  methods: {
    /**
     * Validates loan purposes
     *
     * @isPublic
     */
    validate() {
      const totalPurposeAmount =
        this.purposes?.reduce((acc, cur) => (acc += cur.amount), 0) || 0;

      if (!this.purposes?.length) {
        return {
          valid: false,
          message: 'At least one loan purpose is required to proceed.'
        };
      }

      if (totalPurposeAmount != this.totalLoanAmount) {
        return {
          valid: false,
          message:
            'The total of all loan purposes does not equal the total loan amount. Please add or edit one or more loan purposes.'
        };
      }

      const hasMainPurpose = this.purposes.filter(
        (el) => el.isMainPurpose
      ).length;
      if (!hasMainPurpose) {
        return {
          valid: false,
          message:
            'Please mark one of the loan purpose as the main purpose to proceed.'
        };
      }
      return { valid: true };
    },

    /**
     * Opens modal to edit or add purpose
     *
     * @param { {id?: string, purpose: string, amount: number, isMainPurpose: boolean} } item - a loan purpose
     */
    openModal(item) {
      this.$refs.validationObserver?.reset();
      this.item = cloneDeep(item) || {};

      //Set purpose amount to loan amount if it is the first loan purpose.
      if (!this.purposes.length && !this.item.purpose) {
        this.item.amount = this.totalLoanAmount;
        this.item.isMainPurpose = true;
      }

      this.purposeDialog = true;
    },

    /**
     * Close loan purpose modal
     */
    closeModal() {
      this.purposeDialog = false;
      this.$refs.validationObserver?.reset();
    },

    /**
     * Save new loan purpose details or update an existing loan purpose
     */
    async saveItem() {
      const valid = await this.$refs.validationObserver.validate();
      if (!valid) return;

      this.item.amount = +this.item.amount;
      const purpose = this.loanPurposeTypes.find(
        (el) => el.name == this.item.purpose
      );

      if (this.item.id) {
        const index = this.purposes.findIndex((el) => el.id == this.item.id);
        this.item.code = purpose.code || this.item.code;
        this.purposes.splice(index, 1, { ...this.item });
        this.item.isMainPurpose = !!this.item.isMainPurpose;
      } else {
        this.item.id = v4();
        this.item.isMainPurpose = !!this.item.isMainPurpose;
        this.item.code = purpose.code;
        this.purposes = [...this.loan.loanPurposes, this.item];
      }

      //loop thru and update purpose amount based on loan amount
      const mainIndex = this.loan.loanPurposes.findIndex(
        (el) => el.id != this.item.id && el.isMainPurpose
      );

      if (mainIndex > -1) {
        const purpose = this.purposes[mainIndex];
        purpose.isMainPurpose = false;
        this.purposes.splice(mainIndex, 1, purpose);
      }

      this.$emit('updated', this.purposes);
      this.closeModal();
    },

    /**
     * Remove an existing loan purpose
     *
     * @param { {id: string } } item - a loan purpose with id
     */
    removeItem(item) {
      this.confirmationTitle = `Confirm Deletion`;
      this.confirmationMessage = `Are you sure you want to delete purpose type '${item.purpose}'?`;
      this.$refs.confirmationModal.show(async () => {
        this.purposes = this.purposes.filter((el) => el.id !== item.id);
        this.$emit('updated', this.purposes);
      });
    }
  }
};
</script>
